import config from '@/config'
import sab from '@/api/sab'
import AESCipher from '@/js/phpAesCipher'
import store from '@/store'
import { getViuBypassDefaultCountryCode } from '@/config/viuRegionsFromEnv'
import { getCachedIpGeoSnapshot, getIpGeoSnapshot } from '@/js/mtnGhanaGeo'
import authenticationService from '@/js/AuthenticationService'
import i18n from '@/i18n'

/** sessionStorage key: outbound Viu redirect already fired this tab session (see maybeRedirectViuClient). */
export const VIU_CLIENT_REDIRECT_FLAG = 'viu_client_redirect_v1'

/** sessionStorage: ISO country code of the Viu region used for outbound HE (decrypt + dial code on return). */
export const VIU_HE_ACTIVE_REGION_KEY = 'viu_he_active_region_v1'
/** sessionStorage: carrier code of the Viu region used for outbound HE, when known (debugging/analytics). */
export const VIU_HE_ACTIVE_CARRIER_KEY = 'viu_he_active_carrier_v1'
/** sessionStorage: app route to restore after successful VIU token login. */
export const VIU_POST_LOGIN_PATH_KEY = 'viu_post_login_path_v1'

/**
 * sessionStorage: the missing-MSISDN HE retry already fired this tab session.
 *
 * Deliberately NOT `VIU_CLIENT_REDIRECT_FLAG`: that one is set on the *outbound* leg
 * (`maybeRedirectViuClient`), so it is already `'1'` by the time the partner sends the user back —
 * reusing it would block this retry on every normal round trip and only ever allow it on cold
 * direct-hash landings. See `retryViuHeForMissingMsisdn`.
 */
export const VIU_MISSING_MSISDN_RETRY_FLAG = 'viu_missing_msisdn_retry_v1'

/** Query params (first match wins) that let QA/partner links force a carrier, e.g. `?carrier=telecel`. */
const VIU_CARRIER_OVERRIDE_QUERY_PARAMS = ['carrier', 'operator']

function isSafeViuPostLoginPath(pathValue) {
  const path = String(pathValue || '').trim()
  if (!path?.startsWith('/')) {
    return false
  }
  if (/^\/login(\/|$)/.test(path)) {
    return false
  }

  const errorPath = (config.viu?.partnerErrorPath) || '/header-enrichment/error'
  if (path === errorPath || path.startsWith(`${errorPath}?`)) {
    return false
  }

  return true
}

export function rememberViuPostLoginPath(pathnameAndSearch) {
  if (typeof sessionStorage === 'undefined') {
    return
  }
  const value = String(pathnameAndSearch || '').trim()
  if (!isSafeViuPostLoginPath(value)) {
    return
  }
  sessionStorage.setItem(VIU_POST_LOGIN_PATH_KEY, value)
}

export function consumeViuPostLoginPath() {
  if (typeof sessionStorage === 'undefined') {
    return null
  }
  const raw = sessionStorage.getItem(VIU_POST_LOGIN_PATH_KEY)
  sessionStorage.removeItem(VIU_POST_LOGIN_PATH_KEY)
  if (!isSafeViuPostLoginPath(raw)) {
    return null
  }
  return raw
}

/**
 * Same destination shape as email/phone login (`LoginAuthContainer.goToBrowse`): named `browse`
 * with params/query, or a stored pathname+search from HE outbound. Drops `hash` / `he_hash` so
 * the partner callback query does not linger.
 *
 * @param {string|null|undefined} consumedPath - result of {@link consumeViuPostLoginPath}
 * @param {{ params?: object, query?: object }} currentRoute - typically `this.$route`
 * @returns {object} Vue Router location (`{ name, query }` for browse, or `{ path, query }` when restoring a stored path)
 */
export function resolveViuPostLoginRouterLocation(consumedPath, currentRoute) {
  const clearedQuery = { ...(currentRoute && currentRoute.query ? currentRoute.query : {}) }
  delete clearedQuery.hash
  delete clearedQuery.he_hash

  const stored = consumedPath != null ? String(consumedPath).trim() : ''
  if (!stored) {
    // Do not pass `/login/:type?` params into `browse` — unknown params can break route matching and
    // trigger `authenticationGuard` → `next(false)` (URL stuck on `/login` while Vuex is authenticated).
    return { name: 'browse', query: clearedQuery }
  }

  const q = stored.indexOf('?')
  const pathOnly = q === -1 ? stored : stored.slice(0, q)
  const search = q === -1 ? '' : stored.slice(q + 1)
  const query = {}
  if (search) {
    new URLSearchParams(search).forEach((value, key) => {
      query[key] = value
    })
  }
  delete query.hash
  delete query.he_hash

  return { path: pathOnly, query }
}

export function clearViuHeSessionMarkers() {
  if (typeof sessionStorage === 'undefined') {
    return
  }
  sessionStorage.removeItem(VIU_CLIENT_REDIRECT_FLAG)
  sessionStorage.removeItem(VIU_HE_ACTIVE_REGION_KEY)
  sessionStorage.removeItem(VIU_HE_ACTIVE_CARRIER_KEY)
  sessionStorage.removeItem(VIU_MISSING_MSISDN_RETRY_FLAG)
}

function getViuRegions() {
  const viu = config.viu || {}
  const list = viu.regions
  return Array.isArray(list) && list.length ? list : []
}

function regionMatchesCarrier(region, carrier) {
  if (!carrier) {
    return false
  }
  return String(region.carrier || '').toUpperCase() === carrier
}

/**
 * Reads a QA/partner-link carrier override from the query string (`?carrier=` / `?operator=`),
 * e.g. so a Telecel-tagged campaign link can skip IP/ASN detection entirely.
 * @param {string} [search] defaults to `window.location.search`
 * @returns {string} uppercased carrier code, or '' if none present
 */
export function getViuCarrierOverrideFromUrl(search) {
  const raw = search != null
    ? search
    : (typeof window !== 'undefined' && window.location ? window.location.search : '')
  if (!raw) {
    return ''
  }
  try {
    const params = new URLSearchParams(raw)
    for (const key of VIU_CARRIER_OVERRIDE_QUERY_PARAMS) {
      const value = params.get(key)
      if (value && value.trim()) {
        return value.trim().toUpperCase()
      }
    }
  } catch {
    // ignore malformed query strings
  }
  return ''
}

/**
 * Resolves the mobile carrier from an IP geo snapshot's ASN via `config.viu.carrierAsnMap`
 * (built from `VUE_APP_VIU_CARRIER_ASN_MAP_JSON` — see `viuRegionsFromEnv.js`). This only works
 * reliably while the user is on that carrier's own mobile data, which is also the only case HE
 * itself can succeed in (the gateway enriches the request from on-network traffic).
 * @param {{ asn?: string|null }|null|undefined} geo
 * @returns {string} uppercased carrier code, or '' if unresolved
 */
export function resolveViuCarrierFromGeoSnapshot(geo) {
  const map = (config.viu && config.viu.carrierAsnMap) || {}
  const asn = geo && geo.asn ? String(geo.asn).toUpperCase() : ''
  if (!asn || !map[asn]) {
    return ''
  }
  return String(map[asn]).toUpperCase()
}

/**
 * Picks the region row for a country, disambiguating multiple carriers on that country (see
 * `viuRegionsFromEnv.js` file header). Preference order: exact carrier match, then the country's
 * untagged/default row, then simply the first configured row for that country.
 * @param {string} iso ISO country code
 * @param {string} [carrier] uppercased carrier code from override/ASN detection
 */
function findViuRegionByCountry(iso, carrier) {
  const code = String(iso || '').toUpperCase()
  if (!code) {
    return null
  }
  const candidates = getViuRegions().filter(r => r.countryCode === code)
  if (!candidates.length) {
    return null
  }
  if (carrier) {
    const exact = candidates.find(r => regionMatchesCarrier(r, carrier))
    if (exact) {
      return exact
    }
  }
  return candidates.find(r => !r.carrier) || candidates[0]
}

/**
 * Same disambiguation as {@link findViuRegionByCountry}, restricted to rows that opted into geo
 * bypass (QA). Kept separate so bypass mode still only ever picks a `clientRedirectBypassGeo: true`
 * row, while remaining carrier-aware (e.g. `?carrier=telecel` during bypass QA).
 */
function findBypassEnabledViuRegionByCountry(iso, carrier) {
  const code = String(iso || '').toUpperCase()
  if (!code) {
    return null
  }
  const candidates = getViuRegions().filter(r => r.countryCode === code && r.clientRedirectBypassGeo === true)
  if (!candidates.length) {
    return null
  }
  if (carrier) {
    const exact = candidates.find(r => regionMatchesCarrier(r, carrier))
    if (exact) {
      return exact
    }
  }
  return candidates.find(r => !r.carrier) || candidates[0]
}

/**
 * Resolves the region row a partner `productcode` belongs to (product codes are unique per
 * carrier/market row in `VUE_APP_VIU_REGIONS_JSON`, e.g. `800006` → KSA / STC).
 *
 * This is the *validation* step for the payload's fifth field: rather than trusting its position,
 * we only accept a value that matches a configured row. An absent, unknown, or malformed code
 * returns null, and callers fall back to existing behavior (the partner error page).
 *
 * @param {string} productCode
 * @returns {{ countryCode: string, apiBaseUrl: string, productCode: string, carrier: string|null }|null}
 */
export function findViuRegionByProductCode(productCode) {
  const code = String(productCode || '').trim()
  if (!code) {
    return null
  }
  return getViuRegions().find(r => String(r.productCode || '').trim() === code) || null
}

/** True if any built region allows skipping IP geo for QA (see `viuRegionsFromEnv.js`). */
export function anyViuHeRegionBypassesGeo() {
  return getViuRegions().some(r => r.clientRedirectBypassGeo === true)
}

/**
 * Looks up the `xApiKey` of the region row that was actually used for the outbound HE redirect
 * this session (tracked via `VIU_HE_ACTIVE_REGION_KEY` / `VIU_HE_ACTIVE_CARRIER_KEY`). Returns ''
 * when no active region is known (e.g. a cold direct-hash landing that never went through our own
 * outbound redirect) or when that region doesn't define its own `xApiKey` — callers should fall
 * back to the global `config.viu.xApiKey` in that case.
 *
 * Key selection is independent of the `carrier` tag: any region row that defines an `xApiKey`
 * uses it; a row without one falls back to the global key. The stored carrier is still used to
 * pick the correct row for multi-carrier countries, but no longer gates whether the row's key is
 * used (so single-carrier/untagged rows can carry their own key too).
 * @returns {string}
 */
export function resolveActiveViuRegionXApiKey() {
  if (typeof sessionStorage === 'undefined') {
    return ''
  }
  const countryCode = sessionStorage.getItem(VIU_HE_ACTIVE_REGION_KEY) || ''
  const carrier = sessionStorage.getItem(VIU_HE_ACTIVE_CARRIER_KEY) || ''
  if (!countryCode) {
    // No region resolved this session (e.g. a cold direct-hash landing that never went through
    // our own outbound redirect) — use the global key.
    return ''
  }
  const region = findViuRegionByCountry(countryCode, carrier)
  return (region && typeof region.xApiKey === 'string' && region.xApiKey.trim()) || ''
}

/** API status in decrypted payload: 101 = success (see partner spec). */
export const VIU_STATUS_SUCCESS = 101

/** Fourth field `usertype`: only these may proceed to partner phone login (with MSISDN + success). */
const VIU_SUBSCRIBER_USER_TYPES = new Set(['postpaid', 'prepaid'])

const VIU_STATUS_I18N_KEYS = {
  109: 'invalidCredentials',
  110: 'invalidHash',
  115: 'serverError',
  119: 'requestNotAllowed',
  121: 'heFailure',
  122: 'invalidRequestParameters',
  123: 'apiMaintenance'
}

const LEGACY_STATUS_TO_I18N_KEY = {
  'he failure': 'heFailure',
  'he failure.': 'heFailure',
  'invalid ip': 'invalidIp',
  'server error': 'serverError',
  'request timeout': 'requestTimeout'
}

/**
 * Phones and tablets. Still excludes typical desktop/laptop web browsers (no Viu redirect from
 * desktop Chrome/Safari/Firefox on macOS/Windows/Linux without touch/tablet UAs).
 */
export function isMobileOrTabletForViuRedirect(bypassGeo) {
  if (typeof navigator === 'undefined') {
    return false
  }

  const ua = navigator.userAgent || ''

  if (/iPhone|iPod|Android.+Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return true
  }

  // Android Chrome (incl. reduced / variant UAs that omit the substring "Mobile")
  if (/\(Linux; Android /i.test(ua) && /\bChrome\/[0-9.]+ Mobile Safari/i.test(ua)) {
    return true
  }

  if (/iPad/i.test(ua)) {
    return true
  }

  if (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1) {
    if (!/iPhone|iPod/.test(ua)) {
      return true
    }
  }

  if (/\bAndroid\b/i.test(ua) && !/\bMobile\b/i.test(ua)) {
    return true
  }

  if (/Tablet|PlayBook|Kindle|Silk\/|SM-T\d|SM-P\d|Galaxy Tab|Nexus [79]|KFAPWI|KFMAWI/i.test(ua)) {
    return true
  }

  if (/Windows NT/i.test(ua) && /Touch|Tablet PC|ARM;/i.test(ua)) {
    return true
  }

  const uaData = navigator.userAgentData
  if (uaData?.mobile === true) {
    return true
  }

  if (!bypassGeo) {
    return false
  }

  return false
}

/**
 * Veniso / partner redirect when hash validation fails (see spec).
 * Example: ?redirected=true&errorDesc=[message] (optional legacy errorCode for mapping)
 */
/**
 * Builds the Viu HE entry URL.
 * - Preferred: `?hash=…` (AES-128-CBC over `action|productCode|rurl`, same as PHP).
 * - Fallback: `?action=&productcode=&rurl=` when Web Crypto is unavailable (e.g. http://192.168.x.x
 *   is not a secure context, so `crypto.subtle` is missing — common on LAN dev testing).
 */
/**
 * @param {string} [rurl] return URL
 * @param {{ apiBaseUrl: string, productCode: string, heAction?: string }} regionRow from `config.viu.regions`. AES uses shared `config.viu.aesKey` / `config.viu.aesIv`.
 */
export async function buildViuHeaderEnrichmentUrl(rurl, regionRow) {
  if (!regionRow || typeof regionRow !== 'object') {
    return ''
  }

  const viu = config.viu || {}

  const raw = String(regionRow.apiBaseUrl || '').trim()
  if (!raw) {
    return ''
  }

  let url
  try {
    const normalized = /^https?:\/\//i.test(raw) ? raw : `http://${raw}`
    url = new URL(normalized)
  } catch {
    return ''
  }

  if (!url.pathname || url.pathname === '/') {
    url.pathname = '/api/'
  }

  const returnUrl =
    rurl ||
    (typeof window !== 'undefined' && window.location
      ? `${window.location.origin}${window.location.pathname}${window.location.search || ''}`
      : '')

  const action = (regionRow.heAction && String(regionRow.heAction).trim()) || 'HE'
  const productCode = String(regionRow.productCode || '').trim()
  if (!productCode) {
    return ''
  }

  const plaintext = `${action}|${productCode}|${encodeURIComponent(returnUrl)}`

  const key = viu.aesKey
  const iv = viu.aesIv
  const canBuildHash = Boolean(key && iv)

  if (canBuildHash) {
    try {
      const hashValue = await AESCipher.encrypt(key, iv, plaintext)
      return `${url.origin}${url.pathname}?hash=${hashValue}`
    } catch {
      // fall through to plaintext query
    }
  }

  url.search = ''
  url.searchParams.set('action', action)
  url.searchParams.set('productcode', productCode)
  url.searchParams.set('rurl', encodeURIComponent(returnUrl))

  return url.toString()
}

/**
 * Outbound HE: on phones/tablets only, redirect to the Viu URL for the user's IP country when that
 * country is listed in `config.viu.regions`. Geo bypass is read from each region row
 * (`clientRedirectBypassGeo`); when any region enables it, QA can use `VUE_APP_VIU_BYPASS_GEO_REGION`.
 *
 * When a country has more than one carrier row (e.g. MTN Ghana + Telecel Ghana, both `GH`), the
 * carrier is resolved before picking a region: an explicit `?carrier=`/`?operator=` override wins,
 * otherwise the user's IP ASN is checked against `config.viu.carrierAsnMap`. If neither resolves a
 * carrier, the country's untagged/default row is used — preserving existing single-carrier behavior.
 * @returns {Promise<boolean>} true if navigation was triggered
 */
export async function maybeRedirectViuClient() {
  const viu = config.viu || {}

  if (!viu.clientRedirect) {
    return false
  }

  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return false
  }

  if (sessionStorage.getItem(VIU_CLIENT_REDIRECT_FLAG) === '1') {
    return false
  }

  const regions = getViuRegions()
  if (!regions.length) {
    return false
  }

  const bypassAllowed = anyViuHeRegionBypassesGeo()
  if (!isMobileOrTabletForViuRedirect(bypassAllowed) && !config.viu.allowDesktopHeCallback) {
    return false
  }

  const carrierOverride = getViuCarrierOverrideFromUrl()

  let targetRegion = null

  if (bypassAllowed) {
    const iso = getViuBypassDefaultCountryCode(regions)
    targetRegion = findBypassEnabledViuRegionByCountry(iso, carrierOverride)
  }

  if (!targetRegion) {
    let geo = getCachedIpGeoSnapshot()
    if (!geo || !geo.countryCode) {
      geo = await getIpGeoSnapshot()
    }
    const cc = geo && geo.countryCode ? String(geo.countryCode).toUpperCase() : ''
    const carrier = carrierOverride || resolveViuCarrierFromGeoSnapshot(geo)
    targetRegion = findViuRegionByCountry(cc, carrier)
  }

  if (!targetRegion) {
    return false
  }

  const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search || ''}`
  const target = await buildViuHeaderEnrichmentUrl(returnUrl, targetRegion)
  if (!target) {
    return false
  }

  rememberViuPostLoginPath(`${window.location.pathname}${window.location.search || ''}`)
  sessionStorage.setItem(VIU_CLIENT_REDIRECT_FLAG, '1')
  sessionStorage.setItem(VIU_HE_ACTIVE_REGION_KEY, targetRegion.countryCode)
  if (targetRegion.carrier) {
    sessionStorage.setItem(VIU_HE_ACTIVE_CARRIER_KEY, targetRegion.carrier)
  }
  window.location.assign(target)
  return true
}

/**
 * Current location as an absolute URL with the inbound `hash` / `he_hash` params removed.
 *
 * Required before re-entering HE: `buildViuHeaderEnrichmentUrl` derives `rurl` from
 * `window.location`, which still carries the hash we just failed on. Sending that back means the
 * partner returns to `…?hash=OLD&hash=NEW`; Vue Router surfaces a repeated query param as an array,
 * so `parseViuHashQuery`'s `String(q.hash)` yields `"OLD,NEW"` and decryption fails outright.
 * @returns {string}
 */
function buildCurrentUrlWithoutViuHash() {
  if (typeof window === 'undefined' || !window.location) {
    return ''
  }
  const { origin, pathname, search } = window.location
  let qs = ''
  try {
    const params = new URLSearchParams(search || '')
    params.delete('hash')
    params.delete('he_hash')
    const rest = params.toString()
    qs = rest ? `?${rest}` : ''
  } catch {
    qs = ''
  }
  return `${origin}${pathname}${qs}`
}

/**
 * Recovery for an HE response that carries no MSISDN: the partner did not identify the subscriber,
 * so there is nobody to log in. The payload's `productcode` tells us which carrier/market the
 * transaction belongs to, so we re-run header enrichment against precisely that region instead of
 * dead-ending on the error page. This matters most where several carriers share one country (KSA
 * runs Zain/STC/Mobily), since the first attempt may simply have hit the wrong gateway.
 *
 * Status is deliberately NOT consulted: the partner's documented sample for this case is
 * `|28|121|na|800004`, i.e. status 121 (HE failure) rather than 101, so gating on success would
 * never fire for the payloads actually sent. Callers keep their existing error handling for
 * whatever status came back — this only runs ahead of it.
 *
 * `usertype` is deliberately NOT consulted either — a payload carrying a usable MSISDN never
 * reaches here, and gating on `na` would change behavior for users who sign in successfully today.
 *
 * Fires at most once per tab (`VIU_MISSING_MSISDN_RETRY_FLAG`): header enrichment cannot succeed
 * off-carrier (e.g. the user is on WiFi), so an unguarded retry would bounce forever.
 *
 * @param {{ productCode?: string }} parsed decrypted payload from `parseViuPipePayload`
 * @returns {Promise<{ redirected: boolean, productCode: string, countryCode: string, carrier: string, reason: string }>}
 */
export async function retryViuHeForMissingMsisdn(parsed) {
  const productCode = String((parsed && parsed.productCode) || '').trim()
  const result = { redirected: false, productCode, countryCode: '', carrier: '', reason: '' }

  const viu = config.viu || {}
  if (viu.missingMsisdnRetryEnabled === false) {
    result.reason = 'disabled'
    return result
  }

  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    result.reason = 'unavailable'
    return result
  }

  if (!productCode) {
    result.reason = 'noProductCode'
    return result
  }

  const region = findViuRegionByProductCode(productCode)
  if (!region) {
    result.reason = 'unknownProductCode'
    return result
  }

  result.countryCode = region.countryCode || ''
  result.carrier = region.carrier || ''

  if (sessionStorage.getItem(VIU_MISSING_MSISDN_RETRY_FLAG) === '1') {
    result.reason = 'alreadyRetried'
    return result
  }

  const returnUrl = buildCurrentUrlWithoutViuHash()
  const target = await buildViuHeaderEnrichmentUrl(returnUrl, region)
  if (!target) {
    result.reason = 'urlBuildFailed'
    return result
  }

  sessionStorage.setItem(VIU_MISSING_MSISDN_RETRY_FLAG, '1')
  // Region/carrier drive the per-carrier `xApiKey` on the return leg (`resolveActiveViuRegionXApiKey`).
  sessionStorage.setItem(VIU_HE_ACTIVE_REGION_KEY, region.countryCode)
  if (region.carrier) {
    sessionStorage.setItem(VIU_HE_ACTIVE_CARRIER_KEY, region.carrier)
  }

  result.redirected = true
  result.reason = 'redirected'
  window.location.assign(target)
  return result
}

export function parseVenisoPartnerErrorQuery(query) {
  if (!query || typeof query !== 'object') {
    return null
  }
  const redirected = query.redirected === 'true' || query.redirected === true
  const hasErrorFields =
    query.errorCode != null ||
    query.errorDesc != null ||
    query.error_hash != null

  if (!redirected && !hasErrorFields) {
    return null
  }

  return {
    redirected: true,
    errorCode: query.errorCode != null ? String(query.errorCode) : '',
    errorDesc:
      query.errorDesc != null
        ? String(query.errorDesc)
        : query.error_desc != null
          ? String(query.error_desc)
          : '',
    errorHash: ''
  }
}

/**
 * Reads the encrypted HE callback from the query string.
 * Prefer `?hash=` (partner spec). Optional alias: `?he_hash=` (same value).
 *
 * Vue Router exposes both the URL fragment (`$route.hash`) and query params on `$route.query`.
 * A query param literally named `hash` can be missing from `query` in some cases, so we also
 * parse `window.location.search` when available (e.g. history mode on LAN / mobile).
 */
export function parseViuHashQuery(query) {
  const q = query && typeof query === 'object' ? query : {}
  let h = ''
  if (q.hash != null && String(q.hash).trim() !== '') {
    h = String(q.hash).trim()
  } else if (q.he_hash != null && String(q.he_hash).trim() !== '') {
    h = String(q.he_hash).trim()
  }
  if (h === '' && typeof window !== 'undefined' && window.location && window.location.search) {
    try {
      const params = new URLSearchParams(window.location.search)
      h = (params.get('hash') || params.get('he_hash') || '').trim()
    } catch {
      h = ''
    }
  }
  return h === '' ? null : h
}

/**
 * HE callback ciphertext: reads `?hash=` / `?he_hash=` like {@link parseViuHashQuery}.
 * For **local QA only**, set `VUE_APP_VIU_DEBUG_HASH_OVERRIDE` (see `config.viu.debugHashOverride`):
 * when the partner already returned *some* hash on the URL, replace it with your value (e.g. from
 * `npm run viu:test-urls` / `scripts/viu-generate-test-hash.mjs`) so decrypt/login runs against
 * known-good ciphertext while still exercising the real HE return URL.
 */
export function resolveViuHeHashForProcessing(query) {
  const fromUrl = parseViuHashQuery(query)
  const viu = config.viu || {}
  const override = typeof viu.debugHashOverride === 'string' ? viu.debugHashOverride.trim() : ''
  if (!override || !fromUrl) {
    return fromUrl
  }
  return override
}

export async function decryptViuHash(hashParam) {
  const viu = config.viu || {}
  const key = viu.aesKey
  if (!key) {
    throw new Error('Viu AES key not configured')
  }
  return AESCipher.decrypt(key, String(hashParam))
}

/**
 * Decrypted plaintext: msisdn|htxnid|status|usertype
 * `status` is typically a numeric code (e.g. 101 = success); legacy text "success" is still accepted.
 * `usertype` is expected to be `postpaid`, `prepaid`, or `na` (only postpaid/prepaid may sign in via HE).
 */
export function parseViuPipePayload(plain) {
  const parts = String(plain).split('|')
  const statusRaw = (parts[2] || '').trim()
  const digitsOnly = /^\d+$/.test(statusRaw)
  const statusCode = digitsOnly ? parseInt(statusRaw, 10) : null
  return {
    msisdn: (parts[0] || '').trim(),
    htxnid: (parts[1] || '').trim(),
    statusRaw,
    statusCode: Number.isFinite(statusCode) ? statusCode : null,
    status: statusRaw.toLowerCase(),
    userType: (parts[3] || '').trim(),
    /**
     * Fifth field, *appended* by the partner per spec
     * (`hash(msisdn|htxnid|status|usertype|productcode)`). Absent on older/other payloads, which
     * simply yields '' — every field above keeps its index, so 4-field payloads are unaffected.
     * Never trusted positionally: `findViuRegionByProductCode` only accepts a value that matches a
     * configured region row. See `retryViuHeForMissingMsisdn`.
     */
    productCode: (parts[4] || '').trim()
  }
}

export function isViuResponseSuccess(parsed) {
  if (parsed.statusCode === VIU_STATUS_SUCCESS) {
    return true
  }
  return (parsed.statusRaw || '').toLowerCase() === 'success'
}

/**
 * Partner sends `postpaid`, `prepaid`, or `na`. Login is allowed only for postpaid/prepaid with MSISDN.
 */
export function isViuUserTypeEligibleForPartnerLogin(parsed) {
  const u = (parsed.userType || '').toLowerCase().trim()
  return VIU_SUBSCRIBER_USER_TYPES.has(u)
}

/**
 * Vue i18n key under `auth.viuStatus.*` for error UI.
 */
export function getViuFailureMessageI18nKey(parsed) {
  const code = parsed.statusCode
  if (typeof code === 'number' && Number.isFinite(code)) {
    if (code === VIU_STATUS_SUCCESS) {
      return 'missingMsisdn'
    }
    if (VIU_STATUS_I18N_KEYS[code]) {
      return VIU_STATUS_I18N_KEYS[code]
    }
    return 'unknown'
  }
  const s = (parsed.statusRaw || '').toLowerCase().trim()
  if (s && LEGACY_STATUS_TO_I18N_KEY[s]) {
    return LEGACY_STATUS_TO_I18N_KEY[s]
  }
  if (!parsed.msisdn) {
    return 'missingData'
  }
  return 'genericFailure'
}

/** Local-only sign out (no logout API call) so a VIU hash login never reuses another user's session. */
function clearLocalSessionForViuLogin() {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
  }
  store.commit('authentication/SET_AUTHENTICATED', false)
  store.dispatch('user/unset')
}

export async function loginWithViuMsisdn(msisdn) {
  const value = String(msisdn || '').trim()
  if (!value) {
    return { ok: false, invalidMsisdn: true }
  }

  const viu = config.viu || {}
  // Global key takes priority; only fall back to the active region's per-row xApiKey
  // when no global VUE_APP_VIU_X_API_KEY is configured.
  const xApiKey =
    (typeof viu.xApiKey === 'string' && viu.xApiKey.trim()) ||
    resolveActiveViuRegionXApiKey() ||
    ''

  // The HE hash identifies who is on this device now, so it replaces any existing session (e.g. an
  // earlier test user). Clearing first also stops old tokens from passing the token check below.
  clearLocalSessionForViuLogin()

  try {
    const body = await sab.loginWithViuMsisdn({
      msisdn: value
    }, {
      xApiKey
    })
    if (body?.success !== true) {
      return {
        ok: false,
        invalidMsisdn: false,
        serverErrorMessage: 'Invalid login response'
      }
    }
    if (
      typeof localStorage === 'undefined' ||
      !localStorage.getItem('accessToken') ||
      !localStorage.getItem('refreshToken')
    ) {
      return {
        ok: false,
        invalidMsisdn: false,
        serverErrorMessage: 'Invalid login response'
      }
    }

    try {
      await authenticationService.finalizeSessionAfterExternalLogin()
    } catch {
      // `finalizeSessionAfterExternalLogin` can throw after tokens exist (e.g. Braze or profile).
      // Still establish Vuex session, then **must** load profile — `authorizationGuard` awaits
      // `user.isProfileLoaded` before `next()`, otherwise `replace('/browse')` never completes and
      // the URL stays on `/login` while authenticated.
      await store.dispatch('authentication/establishSessionFromTokens')
      try {
        await store.dispatch('user/verifyProfile')
      } catch {
        // verifyProfile still commits `SET_PROFILE_LOADED` when all SAB calls fail (see user store).
      }
      try {
        const appboy = (await import('appboy-web-sdk')).default
        const userMod = (await import('@/store/modules/user.store')).default
        appboy.changeUser(userMod.state.accountId)
        appboy.openSession()
      } catch {
        // non-fatal
      }
    }
    return { ok: true }
  } catch (err) {
    const status = typeof err.code === 'number' ? err.code : 0

    if (status === 404) {
      return {
        ok: false,
        invalidMsisdn: true,
        serverErrorMessage: i18n.t('auth.viuStatus.phoneLoginNoAccount')
      }
    }

    if (status === 403) {
      return {
        ok: false,
        invalidMsisdn: false,
        subscriptionInactive: true,
        serverErrorMessage: i18n.t('auth.viuStatus.phoneLoginNoSubscription')
      }
    }

    if (status === 422 || status === 500) {
      return {
        ok: false,
        invalidMsisdn: false,
        serverErrorMessage: i18n.t('auth.viuStatus.phoneLoginInternalError')
      }
    }

    return {
      ok: false,
      invalidMsisdn: false,
      serverErrorMessage: i18n.t('auth.viuStatus.phoneLoginInternalError')
    }
  }
}

export function stripViuHashFromRoute(router, query) {
  const nextQuery = { ...(query != null ? query : router.currentRoute.query || {}) }
  delete nextQuery.hash
  delete nextQuery.he_hash
  return router.replace({ path: router.currentRoute.path, query: nextQuery }).catch(() => {})
}

export function buildVenisoErrorQueryFromDecryptedFailure(description) {
  return {
    redirected: 'true',
    errorDesc: description || ''
  }
}

/**
 * Maps partner `errorCode` query (legacy or external) to `auth.viuStatus.*` suffix.
 * Returns `null` → use `auth.viuCallback.invalidHash` for decrypt / invalid-hash cases.
 */
export function viuPartnerErrorCodeToI18nSuffix(codeStr) {
  if (codeStr == null) {
    return null
  }
  const s = String(codeStr).trim()
  if (s === '' || s === 'INVALID_HASH' || s === 'HE_FAILURE') {
    return null
  }
  const n = parseInt(s, 10)
  if (!Number.isNaN(n)) {
    if (n === VIU_STATUS_SUCCESS) {
      return 'missingMsisdn'
    }
    if (VIU_STATUS_I18N_KEYS[n]) {
      return VIU_STATUS_I18N_KEYS[n]
    }
    return 'unknown'
  }
  if (s === 'UNKNOWN') {
    return 'unknown'
  }
  return 'genericFailure'
}
